/**
 * In-memory skin flow navigation state (per session_id).
 * Avoids persisting analysis images/results in sessionStorage on shared kiosks.
 */

import { SALON_FLOW_STATE_PREFIX } from '../config/salonApp';

const TTL_MS = 2 * 60 * 60 * 1000; // 2 hours
const flowBySession = new Map();

function normalizeSessionId(sessionId) {
  if (sessionId == null) return null;
  const s = String(sessionId).trim();
  return s || null;
}

export function saveSkinFlowState(sessionId, state) {
  const sid = normalizeSessionId(sessionId);
  if (!sid || !state || typeof state !== 'object') return;
  flowBySession.set(sid, {
    state: { ...state, sessionId: state.sessionId ?? sid },
    ts: Date.now(),
    sid,
  });
}

export function getSkinFlowState(sessionId) {
  const sid = normalizeSessionId(sessionId);
  if (!sid) return null;
  const entry = flowBySession.get(sid);
  if (!entry) return null;
  const { state, ts, sid: storedSid } = entry;
  if (!state || typeof state !== 'object') return null;
  if (Date.now() - (ts || 0) > TTL_MS) {
    flowBySession.delete(sid);
    return null;
  }
  const stateSessionId = state.sessionId != null ? String(state.sessionId).trim() : storedSid;
  if (stateSessionId && sid && stateSessionId !== sid) return null;
  return state;
}

export function clearSkinFlowState(sessionId) {
  const sid = normalizeSessionId(sessionId);
  if (!sid) return;
  flowBySession.delete(sid);
}

/** Remove legacy sessionStorage flow state from prior builds. */
export function purgeLegacySkinFlowStateStorage() {
  const prefix = SALON_FLOW_STATE_PREFIX;
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(prefix)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}
